import { useCallback } from "react";

export const useEditHTML = () => {
  const handleEditHTML = useCallback(async (html: string): Promise<string> => {
    if (!html) {
      return "";
    }

    const floatConfigs: Record<string, string> = {
      "__se__float-none":
        "margin:auto !important; padding:1px; max-width: 100%;",
      "__se__float-center":
        "margin:auto !important; padding:1px; max-width: 100%;",
      "__se__float-right":
        "margin:auto !important; float: right; padding:1px; max-width: 100%;",
      "__se__float-left":
        "margin:auto !important; float: left; padding:1px; max-width: 100%;",
    };

    let updatedHtml = html;

    Object.entries(floatConfigs).forEach(([floatClass, marginStyle]) => {
      const divRegex = new RegExp(
        `<div[^>]*class=["'][^"']*se-component se-image-container ${floatClass}[^"']*["'][^>]*>([\\s\\S]*?)<\\/div>`,
        "g",
      );

      updatedHtml = updatedHtml.replace(divRegex, (divMatch) => {
        return divMatch.replace(
          /<figure([^>]*)>/g,
          (_figureMatch, figureAttrs) => {
            if (/style=["'][^"']*["']/.test(figureAttrs)) {
              return `<figure${figureAttrs.replace(
                /style=["']([^"']*)["']/,
                (_m: any, styleContent: string) => {
                  if (styleContent.includes(marginStyle.trim()))
                    return `style="${styleContent}"`;
                  return `style="${styleContent.trim().replace(/;?$/, ";")} ${marginStyle}"`;
                },
              )}>`;
            }
            return `<figure${figureAttrs} style="${marginStyle}">`;
          },
        );
      });
    });

    updatedHtml = updatedHtml.replace(/<table([^>]*)>/g, (_match, attrs) => {
      if (/style=["'][^"']*["']/.test(attrs)) {
        return `<table${attrs.replace(
          /style=["']([^"']*)["']/,
          (_m: any, styleContent: string) => {
            if (styleContent.includes("width: 100%"))
              return `style="${styleContent}"`;
            return `style="${styleContent.trim().replace(/;?$/, ";")} width: 100%;"`;
          },
        )}>`;
      }
      return `<table${attrs} style="width: 100%;">`;
    });

    updatedHtml = updatedHtml.replace(
      /<table([^>]*)class=["'][^"']*se-table-layout-fixed[^"']*["'][^>]*>/g,
      (_match, attrs) => {
        if (/style=["'][^"']*["']/.test(attrs)) {
          return `<table${attrs.replace(
            /style=["']([^"']*)["']/,
            (_m: any, styleContent: string) => {
              if (styleContent.includes("table-layout: fixed"))
                return `style="${styleContent}"`;
              return `style="${styleContent.trim().replace(/;?$/, ";")} table-layout: fixed !important;"`;
            },
          )}>`;
        }
        return `<table${attrs} style="table-layout: fixed !important;">`;
      },
    );

    // updatedHtml = updatedHtml.replace(
    //     /<img([^>]*)>/g,
    //     (match, attrs) => {
    //         if (/style=["'][^"']*["']/.test(attrs)) {
    //             return `<img${attrs.replace(
    //                 /style=["']([^"']*)["']/,
    //                 (m: any, styleContent: string) => {
    //                     if (styleContent.includes("max-width")) return `style="${styleContent}"`;
    //                     return `style="${styleContent.trim().replace(/;?$/, ';')} max-width:100%; height:auto; display:block;"`;
    //                 }
    //             )}>`;
    //         } else {
    //             return `<img${attrs} style="max-width:100%; height:auto; display:block;">`;
    //         }
    //     }
    // );

    return updatedHtml;
  }, []);

  return handleEditHTML;
};

const isGeneratedPageElement = (element: Element): boolean =>
  element.classList.contains("background-portrait") ||
  element.classList.contains("background-landscape");

const isPageBreakNode = (node: Node): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  return (
    el.classList.contains("page-break") ||
    el.getAttribute("data-page-break") === "true"
  );
};

const isIgnorableNode = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE) return !(node.textContent || "").trim();
  if (node.nodeType === Node.COMMENT_NODE) return true;
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  if (isPageBreakNode(el)) return false;
  const html = el.innerHTML
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, "")
    .trim();
  const text = (el.textContent || "").replace(/\u00A0/g, "").trim();
  return html === "" && text === "";
};

const pageHasMeaningfulContent = (nodes: Node[]): boolean =>
  nodes.some((n) => !isIgnorableNode(n));

const getInitialSourceNodes = (doc: Document): Node[] => {
  const bodyChildren = Array.from(doc.body.children);
  if (bodyChildren.length === 1) {
    const only = bodyChildren[0];
    if (!isGeneratedPageElement(only) && only.childNodes.length > 0)
      return Array.from(only.childNodes);
  }
  return Array.from(doc.body.childNodes);
};

const splitElementByBr = (doc: Document, element: Element): Node[] => {
  if (!element.querySelector("br")) return [element.cloneNode(true)];

  const temp = doc.createElement("div");
  temp.innerHTML = element.innerHTML;

  const rows: Node[] = [];
  let currentLine = doc.createElement("div");

  const flush = () => {
    const text = (currentLine.textContent || "").replace(/\u00A0/g, "").trim();
    const html = currentLine.innerHTML
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, "")
      .trim();
    if (text || html) rows.push(currentLine);
    currentLine = doc.createElement("div");
  };

  Array.from(temp.childNodes).forEach((child) => {
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).tagName.toLowerCase() === "br"
    ) {
      flush();
      return;
    }
    currentLine.appendChild(child.cloneNode(true));
  });
  flush();

  return rows.length > 0 ? rows : [element.cloneNode(true)];
};

const normalizeNode = (doc: Document, node: Node): Node[] => {
  if (isIgnorableNode(node)) return [];
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || "").trim();
    if (!text) return [];
    const div = doc.createElement("div");
    div.textContent = text;
    return [div];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return [node.cloneNode(true)];

  const el = node as Element;
  if (isPageBreakNode(el)) return [el.cloneNode(true)];
  if (isGeneratedPageElement(el))
    return Array.from(el.childNodes).flatMap((c) => normalizeNode(doc, c));
  if (el.tagName.toLowerCase() === "table") return [el.cloneNode(true)];

  const byBr = splitElementByBr(doc, el);
  if (byBr.length > 1) return byBr;

  if (el.children.length > 0) {
    const childNorm = Array.from(el.childNodes).flatMap((c) =>
      normalizeNode(doc, c),
    );
    if (childNorm.length > 1) return childNorm;
  }

  return [el.cloneNode(true)];
};

const createMeasureRoot = (width: string): HTMLDivElement => {
  const div = window.document.createElement("div");
  div.style.cssText = [
    "position:absolute",
    "left:-99999px",
    "top:0",
    "visibility:hidden",
    "pointer-events:none",
    "box-sizing:border-box",
    `width:${width}`,
    "min-height:0",
    "height:auto",
    "padding:16px",
    "margin:0",
    "background:#fff",
    "overflow:visible",
    "z-index:-1",
    "white-space:normal",
    "word-break:break-word",
    "overflow-wrap:break-word",
    "font-family:serif",
    "font-size:16px",
    "line-height:normal",
  ].join(";");
  window.document.body.appendChild(div);
  return div;
};

const measureHtmlHeight = (root: HTMLDivElement, html: string): number => {
  const wrapper = window.document.createElement("div");
  wrapper.style.cssText = "box-sizing:border-box;width:100%;margin:0;padding:0";
  wrapper.innerHTML = html;
  root.appendChild(wrapper);
  const h = Math.ceil(wrapper.getBoundingClientRect().height);
  root.removeChild(wrapper);
  return h;
};

const getUsablePageHeight = (mode: string): number => {
  const isLandscape = mode === "landscape";
  const page = window.document.createElement("div");
  page.style.cssText = [
    "position:absolute",
    "left:-99999px",
    "top:0",
    "visibility:hidden",
    "pointer-events:none",
    "box-sizing:border-box",
    `width:${isLandscape ? "297mm" : "210mm"}`,
    `height:${isLandscape ? "210mm" : "297mm"}`,
    "padding:16px",
    "margin:0",
    "background:#fff",
    "overflow:hidden",
  ].join(";");
  const inner = window.document.createElement("div");
  inner.style.cssText =
    "box-sizing:border-box;width:100%;height:100%;margin:0;padding:0";
  page.appendChild(inner);
  window.document.body.appendChild(page);
  const h = Math.floor(inner.getBoundingClientRect().height);
  window.document.body.removeChild(page);
  return h;
};

const splitIntoPages = (doc: Document, mode: string): Node[][] => {
  const isLandscape = mode === "landscape";
  const width = isLandscape ? "297mm" : "210mm";
  const usableHeight = getUsablePageHeight(mode);
  const measureRoot = createMeasureRoot(width);

  try {
    const normalized = getInitialSourceNodes(doc)
      .filter((n) => !isIgnorableNode(n))
      .flatMap((n) => normalizeNode(doc, n));

    const pages: Node[][] = [];
    let currentPage: Node[] = [];
    let currentHeight = 0;
    const TOLERANCE = 8;

    for (const node of normalized) {
      if (isPageBreakNode(node)) {
        if (currentPage.length > 0) {
          pages.push(currentPage);
          currentPage = [];
          currentHeight = 0;
        }
        continue;
      }

      const html =
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element).outerHTML
          : node.textContent || "";

      const nodeHeight = measureHtmlHeight(measureRoot, html);

      if (
        currentPage.length === 0 ||
        currentHeight + nodeHeight <= usableHeight + TOLERANCE
      ) {
        currentPage.push(node.cloneNode(true));
        currentHeight += nodeHeight;
      } else {
        pages.push(currentPage);
        currentPage = [node.cloneNode(true)];
        currentHeight = nodeHeight;
      }
    }

    if (currentPage.length > 0) pages.push(currentPage);

    const filtered = pages.filter(pageHasMeaningfulContent);
    return filtered.length === 0 ? [[]] : filtered;
  } finally {
    measureRoot.parentNode?.removeChild(measureRoot);
  }
};

export const useAddBackground = () => {
  const handleAddBackground = useCallback(
    async (
      html: string,
      backgroundUrl: string = "",
      mode: string,
    ): Promise<string> => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || "", "text/html");

      const isLandscape = mode === "landscape";
      const className = isLandscape
        ? "background-landscape"
        : "background-portrait";
      const width = isLandscape ? "297mm" : "210mm";
      const height = isLandscape ? "210mm" : "297mm";

      doc
        .querySelectorAll("style[data-generated='certificate-page-style']")
        .forEach((el) => el.remove());
      doc
        .querySelectorAll(".background-portrait, .background-landscape")
        .forEach((pageEl) => {
          const parent = pageEl.parentNode;
          if (!parent) return;
          while (pageEl.firstChild)
            parent.insertBefore(pageEl.firstChild, pageEl);
          parent.removeChild(pageEl);
        });

      const pages = splitIntoPages(doc, mode).filter(pageHasMeaningfulContent);

      doc.body.innerHTML = "";

      pages.forEach((pageNodes) => {
        const pageDiv = doc.createElement("div");
        pageDiv.className = className;
        pageDiv.setAttribute("data-generated-page", "true");

        if (backgroundUrl) {
          pageDiv.setAttribute(
            "style",
            [
              `background-image:url('${backgroundUrl}')`,
              "background-size:cover",
              "background-position:center",
              "background-repeat:no-repeat",
            ].join(";") + ";",
          );
        }

        pageNodes.forEach((node) => pageDiv.appendChild(node));

        doc.body.appendChild(pageDiv);
      });

      const styleEl = doc.createElement("style");
      styleEl.setAttribute("type", "text/css");
      styleEl.setAttribute("data-generated", "certificate-page-style");
      styleEl.innerHTML = `
        @page {
          margin: 0;
          size: a4 ${mode};
        }
        html, body {
          margin: 0;
          padding: 0;
          width: ${width};
        }
        body {
          box-sizing: border-box;
          word-break: break-word;
          overflow-wrap: break-word;
        }
        .background-portrait,
        .background-landscape {
          box-sizing: border-box;
          width: ${width};
          height: ${height};
          padding: 16px;
          position: relative;
          overflow: hidden;
          page-break-after: always;
          break-after: page;
        }
        .background-portrait:last-child,
        .background-landscape:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        .background-portrait table,
        .background-landscape table {
          width: 100%;
          border-collapse: collapse;
        }
        .background-portrait td,
        .background-portrait th,
        .background-landscape td,
        .background-landscape th {
          box-sizing: border-box;
          word-break: break-word;
          padding: 4px;
        }
        .page-break,
        [data-page-break="true"] {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
        }
      `;

      let head = doc.querySelector("head");
      if (!head) {
        head = doc.createElement("head");
        doc.documentElement.prepend(head);
      }
      head.appendChild(styleEl);

      return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
    },
    [],
  );

  return handleAddBackground;
};
